import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Transition de page : au clic sur un lien interne, un voile aux couleurs
 * du Family's balaye l'écran (logo au centre), puis la nouvelle page
 * apparaît. Respecte prefers-reduced-motion (navigation directe).
 */
const PageTransition = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const overlayRef = useRef(null);
  const pendingRef = useRef(false);

  // Arrivée sur la nouvelle page → le voile se retire vers le haut
  useEffect(() => {
    const ov = overlayRef.current;
    if (!ov || !pendingRef.current) return;
    pendingRef.current = false;
    ov.classList.remove('pt-in');
    ov.classList.add('pt-out');
    const t = setTimeout(() => ov.classList.remove('pt-out'), 560);
    return () => clearTimeout(t);
  }, [location.pathname]);

  // Interception des clics sur les liens internes
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest('a');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href || !href.startsWith('/') || href.startsWith('//')) return;
      if (a.target === '_blank' || a.hasAttribute('download')) return;
      if (href.split('#')[0] === location.pathname) return;

      e.preventDefault();
      const ov = overlayRef.current;
      if (!ov) { navigate(href); return; }
      pendingRef.current = true;
      ov.classList.remove('pt-out');
      ov.classList.add('pt-in');
      setTimeout(() => navigate(href), 430);
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [location.pathname, navigate]);

  return (
    <>
      <div ref={overlayRef} className="page-transition" aria-hidden="true">
        <img src={`${process.env.PUBLIC_URL}/images/logo-familys.png`} alt="" />
      </div>
      <style>{`
        .page-transition {
          position: fixed;
          inset: 0;
          z-index: 99999;
          pointer-events: none;
          transform: translateY(101%);
          background: linear-gradient(160deg, #7c2d12 0%, #ea580c 55%, #dc2626 100%);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .page-transition img {
          width: 120px;
          max-width: 40vw;
          opacity: 0;
          filter: drop-shadow(0 0 22px rgba(255,255,255,.65)) drop-shadow(0 6px 20px rgba(0,0,0,.35));
        }
        .page-transition.pt-in {
          animation: ptIn .43s cubic-bezier(.65,0,.35,1) forwards;
        }
        .page-transition.pt-in img {
          animation: ptLogo .43s ease-out .12s forwards;
        }
        .page-transition.pt-out {
          animation: ptOut .55s cubic-bezier(.65,0,.35,1) forwards;
        }
        .page-transition.pt-out img { opacity: 1; animation: ptLogoOut .3s ease-in forwards; }
        @keyframes ptIn   { from { transform: translateY(101%); } to { transform: translateY(0); } }
        @keyframes ptOut  { from { transform: translateY(0); }   to { transform: translateY(-101%); } }
        @keyframes ptLogo    { from { opacity: 0; transform: scale(.7); } to { opacity: 1; transform: scale(1); } }
        @keyframes ptLogoOut { from { opacity: 1; } to { opacity: 0; transform: scale(1.15); } }

        /* Nouvelle page : apparition en fondu + léger glissement */
        .page-enter { animation: pageEnter .5s ease both; }
        @keyframes pageEnter {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: none; }
        }

        /* Retour tactile : les boutons s'enfoncent au toucher */
        @media (pointer: coarse) {
          a:active, button:active { transform: scale(.96); transition: transform .08s ease; }
        }
        @media (prefers-reduced-motion: reduce) {
          .page-transition { display: none; }
          .page-enter { animation: none; }
        }
      `}</style>
    </>
  );
};

export default PageTransition;
